/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  type CommonProps,
  type PolymorphicCommonProps,
} from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import { BoltIconOutline } from '../icons/generated/heroIcons/BoltIconOutline';
import { ExclamationCircleIconSolid } from '../icons/generated/heroIcons/ExclamationCircleIconSolid';
import { ExclamationTriangleIconSolid } from '../icons/generated/heroIcons/ExclamationTriangleIconSolid';
import { InformationCircleIconSolid } from '../icons/generated/heroIcons/InformationCircleIconSolid';
import { SparklesIconSolid } from '../icons/generated/heroIcons/SparklesIconSolid';
import { XMarkIconOutline } from '../icons/generated/heroIcons/XMarkIconOutline';
import { Typography } from '../typography';

/**
 *
 *
 * Types
 *
 *
 */

type AnnouncementBannerVariant =
  | 'info'
  | 'warning'
  | 'danger'
  | 'discovery'
  | 'neutral';

type AnnouncementBannerLiveRegion = 'status' | 'alert';

type PendingAnnouncement = {
  message: string;
  role: AnnouncementBannerLiveRegion;
};

type AnnouncementBannerCommonProps = {
  /** Shows a status icon. The icon is based on the value of the `variant` prop */
  hasIcon?: boolean;

  /**
   * Decorative icon shown when `hasIcon` is true. Only used for `discovery`
   * and `neutral`; those variants fall back to Sparkles and Bolt. `info`,
   * `warning`, and `danger` always use their status icons.
   */
  icon?: React.ReactNode;

  /** Content to be shown in the announcement banner. */
  children?: React.ReactNode;

  /**
   * Announces the header when the banner appears or its header changes.
   * `status` announces politely, while `alert` interrupts the user. Leave this
   * unset for banners that are present during the page's initial render.
   * This is independent of the visual `variant`.
   */
  liveRegion?: AnnouncementBannerLiveRegion;

  /** The variant of the announcement banner */
  variant?: AnnouncementBannerVariant;
};

type CloseableAnnouncementBannerProps = {
  /** If the banner is closeable. Renders an icon button on the right */
  isCloseable: true;

  /** Event handler for when the close icon is clicked */
  onClose: (
    error?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => void;
};

type NonCloseableAnnouncementBannerProps = {
  isCloseable?: false;
  onClose?: never;
};

type AnnouncementBannerCloseProps =
  | CloseableAnnouncementBannerProps
  | NonCloseableAnnouncementBannerProps;

type AnnouncementBannerProps = AnnouncementBannerCommonProps &
  AnnouncementBannerCloseProps;

const LIVE_REGION_REGISTRATION_DELAY = 100;

const StatusIcon = ({
  icon,
  variant,
}: {
  icon?: React.ReactNode;
  variant: AnnouncementBannerVariant;
}) => {
  const canUseCustomIcon = variant === 'discovery' || variant === 'neutral';
  const iconComponent = (() => {
    if (canUseCustomIcon && icon) {
      return icon;
    }

    switch (variant) {
      case 'warning':
        return <ExclamationTriangleIconSolid />;
      case 'danger':
        return <ExclamationCircleIconSolid />;
      case 'discovery':
        return <SparklesIconSolid />;
      case 'info':
        return <InformationCircleIconSolid />;
      case 'neutral':
        return <BoltIconOutline />;
      default:
        return null;
    }
  })();

  return (
    <span className="ndl-announcement-banner-status-icon" aria-hidden="true">
      {iconComponent}
    </span>
  );
};

const AnnouncementBannerContext = React.createContext<{
  hasIcon: boolean;
  icon?: React.ReactNode;
  variant: AnnouncementBannerVariant;
}>({
  hasIcon: false,
  variant: 'info',
});

/**
 * Short title or message for the announcement.
 */
const AnnouncementBannerHeader = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'span', { children: React.ReactNode }>) => {
  const { hasIcon, icon, variant } = useContext(AnnouncementBannerContext);
  const classes = classNames('ndl-announcement-banner-header', className);
  return (
    <Typography
      as="span"
      variant="body-medium"
      className={classes}
      style={style}
      ref={ref}
      htmlAttributes={htmlAttributes}
      {...restProps}
    >
      {hasIcon && <StatusIcon icon={icon} variant={variant} />}
      {children}
    </Typography>
  );
};

/**
 * Optional actions shown next to the announcement message.
 */
const AnnouncementBannerActions = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', { children: React.ReactNode }>) => {
  const classes = classNames('ndl-announcement-banner-actions', className);
  return (
    <div
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </div>
  );
};

/**
 * Full-width announcement bar intended to sit at the top of its parent.
 * It stays in normal document flow so sibling content is shifted down.
 *
 * @remarks
 * Place `AnnouncementBanner` as the first child of a full-width parent (for
 * example the page shell).
 */
const AnnouncementBannerComponent = <
  TElement extends React.ElementType = 'section',
>({
  as,
  variant = 'info',
  hasIcon = false,
  icon,
  onClose,
  className,
  children,
  isCloseable = false,
  htmlAttributes,
  liveRegion,
  ref,
  ...restProps
}: PolymorphicCommonProps<TElement, AnnouncementBannerProps>) => {
  const Element: React.ElementType = as ?? 'section';
  const contentRef = useRef<HTMLDivElement>(null);
  const [pendingAnnouncement, setPendingAnnouncement] =
    useState<PendingAnnouncement | null>(null);

  const classes = classNames('ndl-announcement-banner', className, {
    'ndl-announcement-banner-closeable': isCloseable,
    'ndl-danger': variant === 'danger',
    'ndl-discovery': variant === 'discovery',
    'ndl-info': variant === 'info',
    'ndl-neutral': variant === 'neutral',
    'ndl-warning': variant === 'warning',
  });

  const handleClose = (
    error:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>,
  ) => {
    if (onClose && typeof onClose === 'function') {
      onClose(error);
    }
  };

  useEffect(() => {
    if (!liveRegion || !contentRef.current) {
      setPendingAnnouncement(null);
      return;
    }

    const header = contentRef.current.querySelector(
      '.ndl-announcement-banner-header',
    );

    if (!header) {
      setPendingAnnouncement(null);
      return;
    }

    let timeoutId: number | undefined;

    const announceHeader = () => {
      window.clearTimeout(timeoutId);
      setPendingAnnouncement(null);

      const message = header.textContent?.trim();

      if (message) {
        timeoutId = window.setTimeout(() => {
          setPendingAnnouncement({ message, role: liveRegion });
        }, LIVE_REGION_REGISTRATION_DELAY);
      }
    };

    const observer = new MutationObserver(announceHeader);
    observer.observe(header, {
      characterData: true,
      childList: true,
      subtree: true,
    });
    announceHeader();

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [liveRegion]);

  const announcement =
    pendingAnnouncement?.role === liveRegion
      ? (pendingAnnouncement?.message ?? '')
      : '';

  return (
    <Element className={classes} ref={ref} {...restProps} {...htmlAttributes}>
      {liveRegion && (
        <span
          className="ndl-announcement-banner-live-region"
          role={liveRegion}
          aria-atomic="true"
        >
          {announcement}
        </span>
      )}
      <div className="ndl-announcement-banner-container" ref={contentRef}>
        <AnnouncementBannerContext.Provider value={{ hasIcon, icon, variant }}>
          {children}
        </AnnouncementBannerContext.Provider>
      </div>
      {isCloseable && (
        <div className="ndl-announcement-banner-close">
          <CleanIconButton
            size="small"
            onClick={handleClose}
            description={null}
            htmlAttributes={{ 'aria-label': 'Close' }}
          >
            <XMarkIconOutline />
          </CleanIconButton>
        </div>
      )}
    </Element>
  );
};

const AnnouncementBanner = Object.assign(AnnouncementBannerComponent, {
  Actions: AnnouncementBannerActions,
  Header: AnnouncementBannerHeader,
});

export { AnnouncementBanner };
